import type { TableRow } from "./spreadsheet-table";

/**
 * Data for the Species Comparison table (Table block, Table Type "Species
 * Comparison"): one spreadsheet row per species + grade. Species, Grade and
 * the optional link/note columns are fixed; every other column is a
 * characteristic, so columns can be added or renamed without code changes.
 */

export const SPECIES = "Species";
export const GRADE = "Grade";
export const SPECIES_LINK = "Species Link";
export const SPECIES_LINK_TEXT = "Species Link Text";
export const NOTE = "Note";

export const SPECIES_REQUIRED_COLUMNS = [SPECIES, GRADE];
const FIXED_COLUMNS = [SPECIES, GRADE, SPECIES_LINK, SPECIES_LINK_TEXT, NOTE];

/** Single-letter cell values drawn as badges; anything else is plain text. */
export const CODES: Record<string, string> = {
  Y: "Allowed",
  N: "Not allowed",
  S: "Slight",
  O: "Occasional",
};

export const codeOf = (value: string) => {
  const code = value.trim().toUpperCase();
  return code in CODES ? code : null;
};

export interface SpeciesGroup {
  species: string;
  link: string;
  linkText: string;
  note: string;
  rows: TableRow[];
  /** Characteristic columns whose value isn't the same on every grade. */
  differs: Set<string>;
}

export const characteristicColumns = (columns: string[]) =>
  columns.filter((column) => !FIXED_COLUMNS.includes(column));

/** Rows grouped by species, in spreadsheet order. Link/note come from the first row that has one. */
export function groupBySpecies(rows: TableRow[], columns: string[]): SpeciesGroup[] {
  const characteristics = characteristicColumns(columns);
  const groups = new Map<string, SpeciesGroup>();
  for (const row of rows) {
    const species = row[SPECIES]?.trim();
    if (!species) continue;
    let group = groups.get(species);
    if (!group) {
      group = { species, link: "", linkText: "", note: "", rows: [], differs: new Set() };
      groups.set(species, group);
    }
    group.link ||= linkHref(row[SPECIES_LINK] ?? "");
    group.linkText ||= row[SPECIES_LINK_TEXT]?.trim() ?? "";
    group.note ||= row[NOTE]?.trim() ?? "";
    group.rows.push(row);
  }
  for (const group of groups.values()) {
    for (const column of characteristics) {
      const values = new Set(group.rows.map((row) => comparable(row[column] ?? "")));
      if (values.size > 1) group.differs.add(column);
    }
  }
  return [...groups.values()];
}

const SITE_HOST = /^(www\.)?forteopenings\.com$/i;
const FILE_EXTENSION = /\.(pdf|docx?|xlsx?|csv|jpe?g|png|gif|svg|webp|zip|dwg)$/i;

/**
 * Turns a Species Link cell into an href, so editors can type a link however
 * they like: "www.example.com/x" and "example.com/x" get https://, links to
 * forteopenings.com become site paths, and a bare path like "wood-veneer-maple"
 * gets a leading slash. Full URLs, paths and mailto:/tel: links are kept.
 */
export function linkHref(value: string): string {
  const link = value.trim();
  if (!link) return "";
  if (link.startsWith("//")) return siteRelative(`https:${link}`);
  if (/^[a-z][a-z\d+.-]*:/i.test(link) || /^[/#?]/.test(link)) return siteRelative(link);
  const host = link.split(/[/?#]/)[0];
  const looksLikeHost =
    /^www\./i.test(host) || (/^[\w-]+(\.[\w-]+)*\.[a-z]{2,}$/i.test(host) && !FILE_EXTENSION.test(host));
  return looksLikeHost ? siteRelative(`https://${link}`) : `/${link}`;
}

/** Links to our own site become paths, so they open in the same tab and environment. */
function siteRelative(href: string): string {
  if (!/^https?:\/\//i.test(href)) return href;
  try {
    const url = new URL(href);
    return SITE_HOST.test(url.hostname) ? `${url.pathname}${url.search}${url.hash}` : href;
  } catch {
    return href;
  }
}

const comparable = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

/** Preview-only warnings about the spreadsheet's contents. */
export function speciesWarnings(groups: SpeciesGroup[], columns: string[]): string[] {
  const warnings: string[] = [];
  const characteristics = characteristicColumns(columns);
  if (!characteristics.length) warnings.push("No characteristic columns were found besides Species and Grade.");
  const single = groups.filter((g) => g.rows.length < 2).map((g) => g.species);
  if (single.length) warnings.push(`Only one grade for: ${single.join(", ")}. Nothing to compare for ${single.length > 1 ? "these" : "it"}.`);
  const unknown = new Set<string>();
  for (const group of groups) {
    for (const row of group.rows) {
      for (const column of characteristics) {
        const value = row[column]?.trim() ?? "";
        if (value.length === 1 && !codeOf(value)) unknown.add(value);
      }
    }
  }
  if (unknown.size) {
    warnings.push(
      `Unknown code${unknown.size > 1 ? "s" : ""} ${[...unknown].map((v) => `"${v}"`).join(", ")} shown as plain text. Badges are Y, N, S and O.`,
    );
  }
  return warnings;
}
